import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { fingerprint } from "../../utilities/fingerprint";
import * as actions from "../../state/actions/";
import logoBlack from "../../assets/tomo_logo_black.svg";
import Header from "../common/Header";
import Stepper from "../common/Stepper";
import ToggleVisibility from "../common/ToggleVisibility";
import SignupDescription from "./SignupDescription";
import { track, identify, identifyHotjarUser } from "../../api/heap";
import isKey from "../../utilities/keyboard-events";
import { ONBOARDING_VERSION_1_0 } from "../../constants";
const axios = require("axios");
const passwordValidator = require("password-validator");

const md5 = require("md5");

const NOT_ACCEPTING_NEW_APPLICATIONS = "We are not currently accepting signups at this time";

class BasicInfo extends Component {
  constructor(props) {
    super(props);
    this.state = {
      internalError: null,
      errorEmail: null,
      emailTip: null
    };
  }

  // TODO: Since we're using HTTPS this might be enough for now,
  // but we should research security. MD5 is not reliable.
  hashPassword = password => {
    return md5(password);
  };

  // TODO: This component might not always be the first to be displayed
  // Have fingerprinting happen on a higher component level
  async componentDidMount() {
    const {
      setFingerprint,
      updateUser,
    } = this.props;
    fingerprint(fp => {
      setFingerprint(fp);
      window.sessionStorage.setItem("fingerprint", fp);
    });

    const urlParams = this.searchParams();
    if (urlParams.user) {
      const {
        updateStep,
      } = this.props;
      const email = decodeURIComponent(urlParams.user);
      updateUser({ email });

      const userCheckPayload = { userId: email.toLowerCase() };

      updateStep("basicInfo", { loading: true });

      let userExists, passwordExists;
      try{
        const { data } = await axios.post("/users/userExists", userCheckPayload);
        userExists = data.userExists;
        passwordExists = data.passwordExists;
      } catch(err) {
        this.setState({
          errorEmail: "Something went wrong with our servers. Please try again later."
        });
      }
      
      updateStep("basicInfo", { loading: false });

      if(userExists && passwordExists){
        window.location.replace("/register");
      }

      // need to create user upon visiting basic info page if they're not in our DB since
      // we rely on this for metrics
      if(!userExists){
        try {
          await axios.post("/users/newUserFromLanding", {
            ...userCheckPayload, 
            configVersion: ONBOARDING_VERSION_1_0, 
            source: "CARD_ONBOARDING", // TODO: Figure out why common-fe imports are broken for onboarding
          });
        } catch (err) {
          this.setState({
            errorEmail: "Something went wrong with our servers. Please try again later."
          });
        }
      }
    }

    if(urlParams.referral) {
      this.setState({ referralCode: urlParams.referral });
    }
  }

  componentWillReceiveProps(nextProps) {
    // BUG: Any input will move the user to the next
    // step if stepIsComplete
    // see: https://trello.com/c/Ua63RQbu
    if (nextProps.step.stepIsComplete) {
      if (this.props.step.skipEmailVerification) {
        this.props.nextStep(null, null, 2);
      } else {
        this.props.nextStep();
      }
    }
  }

  static propTypes = {
    stepNumber: PropTypes.number.isRequired,
    nextStep: PropTypes.func.isRequired,
    previousStep: PropTypes.func.isRequired
  };

  searchParams = () =>
    window.location.search
      .slice(1)
      .split("&")
      .reduce((a, c) => ({ [c.split("=")[0]]: c.split("=")[1], ...a }), {});

  isEmailValid = () => {
    const { email } = this.props.user;
    if (!email) {
      track("ONBOARDING_VALIDATION_ERROR_BASIC_INFO_STEP", {
        message: "empty_email"
      });
      this.setState({
        errorEmail: "Email address is required",
        emailTip: null
      });
      return false;
      // } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
      //   this.setState({ errorEmail: "Invalid email address", emailTip: null });
      //   return false;
    } else {
      this.setState({ errorEmail: null, emailTip: null });
    }
    return true;
  };

  isPasswordValid = () => {
    const { password } = this.props.user;

    if (!password) {
      track("ONBOARDING_VALIDATION_ERROR_BASIC_INFO_STEP", {
        message: "empty_password"
      });
      this.setState({
        errorPassword: "You must choose a password"
      });
      return false;
    } else {
      this.setState({
        errorPassword: null
      });
    }

    const schema = new passwordValidator();

    const minimumCharacters = 8;
    const maximumCharacters = 50;

    schema
      .is()
      .min(minimumCharacters) // Minimum length 8
      .is()
      .max(maximumCharacters) // Maximum length 100
      .has()
      .uppercase() // Must have uppercase letters
      .has()
      .lowercase() // Must have lowercase letters
      .has()
      .digits() // Must have digits
      .has()
      .not()
      .spaces() // Should not have spaces
      .is()
      .not()
      .oneOf(["Passw0rd", "Password123"]); // Blacklist these values

    const result = schema.validate(password, { list: true });

    if (result && result.length === 0) {
      return true;
    } else {
      if (result.includes("min")) {
        this.setState({
          errorPassword: `Password must contain at least ${minimumCharacters} characters`
        });
      } else if (result.includes("max")) {
        this.setState({
          errorPassword: `Password must contain ${maximumCharacters} characters at most`
        });
      } else if (result.includes("uppercase")) {
        this.setState({
          errorPassword: `Password must contain uppercase characters`
        });
      } else if (result.includes("lowercase")) {
        this.setState({
          errorPassword: `Password must contain lowercase characters`
        });
      } else if (result.includes("digits")) {
        this.setState({ errorPassword: `Password must contain a number` });
      } else if (result.includes("spaces")) {
        this.setState({
          errorPassword: `Password must not contain any spaces`
        });
      } else if (result.includes("oneOf")) {
        this.setState({
          errorPassword: `Your password is too generic. Please use a more unique password.`
        });
      }
      track("ONBOARDING_VALIDATION_ERROR_BASIC_INFO_STEP", {
        message: "failed_password_validation"
      });
      return false;
    }
  };

  nameCheck = name => {
    const nameRegex = new RegExp(/[^a-zA-Z0-9' -]/g);
    return (name.length >= 2 && name.length <= 30 && !nameRegex.test(name));
  }

  isFormValid = () => {
    const {
      firstName,
      lastName,
      email, // not used?
      password // not used?
    } = this.props.user;

    const validFirstName = this.nameCheck(firstName);
    const validLastName = this.nameCheck(lastName);

    if (firstName && validFirstName) {
      this.setState({ errorFirstName: null });
    }
    if (lastName && validLastName) {
      this.setState({ errorLastName: null });
    }


    if (!firstName || !lastName || !validFirstName || !validLastName) {
      track("ONBOARDING_VALIDATION_ERROR_BASIC_INFO_STEP", {
        message: "invalid first or last name"
      });
      !firstName && this.setState({ errorFirstName: "First name is required" });
      !lastName && this.setState({ errorLastName: "Last name is required" });
      !validFirstName && this.setState({ errorFirstName: "Invalid name: must be 2-30 characters; letters, spaces, numbers, as well as hyphens (-) and single quote (')" });
      !validLastName && this.setState({ errorLastName: "Invalid name: must be 2-30 characters; letters, spaces, numbers, as well as hyphens (-) and single quote (')" });
      return false;
    }

    return true;
  };

  handlePrevious = () => {
    if (this.props.step.stepIsComplete) {
      this.props.previousStep();
    } else {
      this.save().then(() => {
        this.props.previousStep();
      });
    }
  };

  handleNext = () => {
    this.props.updateStep("basicInfo", { error: NOT_ACCEPTING_NEW_APPLICATIONS });
    return;
    console.log("basicInfo handling next: ", this.props.step);
    if (this.state.transitioning) return;
    this.setState({ transitioning: true });
    const { isEmailValid, isFormValid, isPasswordValid } = this;
    const { setNetworkingError } = this.props;

    if (
      this.props.step.stepIsComplete &&
      isEmailValid() &&
      isFormValid() &&
      isPasswordValid()
    ) {
      setNetworkingError(null);
      if (this.props.step.skipEmailVerification) {
        this.props.nextStep(null, null, 2);
      } else {
        this.props.nextStep();
      }
    } else {
      this.save();
    }
  };

  save = async () => {
    if (
      !this.isEmailValid() ||
      !this.isFormValid() ||
      !this.isPasswordValid()
    ) {
      this.setState({ transitioning: false });
      return;
    }

    const {
      newUserSignup,
      updateStep,
      updateUser,
      fingerprint,
      hydrateStateWithToken,
      user: { email, firstName, lastName, password }
    } = this.props;

    const { referralCode } = this.state;

    // Store this guy when we can
    window.sessionStorage.setItem("userId", email.toLowerCase());

    const hashedPassword = this.hashPassword(password);

    // TODO: This is something that should happen on a higher level component
    if (!fingerprint) {
      console.log("No fingerprint in Redux, checking sessionStorage");
      const sessionFP = window.sessionStorage.getItem("fingerprint");
      sessionFP && this.props.setFingerprint(sessionFP);
    }

    if (this.props.noValidate) {
      await newUserSignup(email.toLowerCase(), {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.toLowerCase(),
        password: hashedPassword,
        referralCode,
        source: "CARD_ONBOARDING" // TODO: Figure out why common-fe imports are broken for onboarding
      });
      updateUser({ firstName: firstName.trim(), lastName: lastName.trim() });
      updateStep("basicInfo", { stepIsComplete: true });
      if (this.props.step.skipEmailVerification) {
        this.props.nextStep(null, null, 2);
      } else {
        this.props.nextStep();
      }
      return;
    }

    updateStep("basicInfo", { loading: true });
    const newUserPayload = {
      firstName,
      lastName,
      email: email.toLowerCase(),
      password: hashedPassword,
      referralCode,
      source: "CARD_ONBOARDING" // TODO: Figure out why common-fe imports are broken for onboarding
    };
    const success = await newUserSignup(
      email.toLowerCase(),
      newUserPayload
    );
    updateStep("basicInfo", { loading: false });
    updateUser({ firstName: newUserPayload.firstName.trim(), lastName: newUserPayload.lastName.trim() });
    if (success) {
      const { email } = this.props.user;
      identify(email);
      identifyHotjarUser({email})
      track("ONBOARDING_COMPLETED_BASIC_INFO_STEP");
      updateStep("basicInfo", { stepIsComplete: true });
      await hydrateStateWithToken({ 
        email
      })
    } else {
      updateStep("basicInfo", { error: NOT_ACCEPTING_NEW_APPLICATIONS });
      // track("ONBOARDING_ERROR_BASIC_INFO_STEP", {
      //   message: "user_already_signed_up"
      // });
      // updateStep("basicInfo", {
      //   error:
      //     "Looks like you already have an account! Please use the link below to sign in."
      // });
      this.setState({ transitioning: false });
    }

  };

  renderMobile = () => {
    const { handleNext, renderDesktop } = this;
    const {
      internalError,
      errorFirstName,
      errorLastName,
      errorEmail,
      errorPassword,
      errorTerms,
      showPassword
    } = this.state;
    const {
      updateUser,
      isMobile,
      user: { firstName, lastName, email, password, ppAccepted, termsAccepted },
      step: { error, loading }
    } = this.props;

    const acceptError = [termsAccepted, ppAccepted].filter(x => x).length !== 2;

    const signedIn = !!localStorage.getItem("jwt");

    const renderSignedOut = () => (
      <div
        className="stepContainer md:stepContainer-mobile sm:stepContainer-mobile"
        style={{ display: "block" }}
      >
        <Header className="md:hidden" />
        <div className="innerStepContainer xl:innerStepContainer-desktop lg:innerStepContainer-desktop md:innerStepContainer-mobile sm:innerStepContainer-mobile">
          <div id="stepHeader" className="md:pt-8 md:pb-3 md:py-8 w-full">
            <Stepper activeStep={1} />
            <img
              src={logoBlack}
              style={{ padding: 0 }}
              className="hidden md:block mx-auto mt-12 w-auto"
            />
            {/*<Logo
              style={{ padding: 0 }}
              className="hidden md:block mx-auto mt-12 w-auto"
            />*/}
          </div>

          <div className="outerStack-mobile sm:outerStack xl:outerStack-mobile sm:outerStack-desktop lg:outerStack-mobile sm:outerStack-desktop md:outerStack-mobile sm:outerStack-mobile sm:outerStack-mobile sm:outerStack-mobile">
            <div className="innerStack">
              <div className="stepDescriptionText">Create your account</div>
              <form>
                {errorFirstName && (
                  <div className="errorText">{errorFirstName}</div>
                )}
                <input
                  autoComplete="new-password"
                  type="text"
                  data-hj-allow
                  name="firstname"
                  value={firstName}
                  placeholder="Legal First Name"
                  className={`inputField inputFieldText form-control data-hj-allow ${errorFirstName &&
                    "redBorder"}`}
                  onChange={e => {
                    updateUser({ firstName: e.target.value });
                  }}
                ></input>
                <br />
                {errorLastName && (
                  <div className="errorText">{errorLastName}</div>
                )}
                <input
                  autoComplete="new-password"
                  type="text"
                  data-hj-allow
                  name="lastname"
                  value={lastName}
                  placeholder="Legal last name"
                  className={`inputField inputFieldText form-control data-hj-allow ${errorLastName &&
                    "redBorder"}`}
                  onChange={e => {
                    updateUser({ lastName: e.target.value });
                  }}
                ></input>
                <br />
                {errorEmail && <div className="errorText">{errorEmail}</div>}
                <input
                  autoComplete="new-password"
                  type="text"
                  data-hj-allow
                  name="email"
                  value={email}
                  placeholder="Email"
                  disabled={!!email}
                  className={`inputField inputFieldText form-control data-hj-allow ${
                    errorEmail ? "redBorder" : ""
                  } text-gray-700`}
                  onChange={e => {
                    updateUser({ email: e.target.value.toLowerCase() });
                  }}
                  onFocus={e => {
                    this.setState({ errorEmail: null, emailTip: null });
                  }}
                ></input>
                <br />
                {errorPassword && (
                  <div className="errorText">{errorPassword}</div>
                )}
                {/* NOTE: Password show/hide button will overlap long passwords on small screens */}
                <input
                  autoComplete="new-password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={password}
                  placeholder="Password"
                  className={`inputField inputFieldText ${errorPassword &&
                    "redBorder"}`}
                  onChange={e => {
                    updateUser({ password: e.target.value });
                  }}
                  onFocus={e => {
                    this.setState({ errorPassword: null });
                  }}
                ></input>
                <div
                  style={{
                    position: "relative",
                    display: "inline-block",
                    height: 0,
                    width: 0
                  }}
                >
                  <ToggleVisibility
                    visible={showPassword}
                    onClick={() => {
                      this.setState({ showPassword: !this.state.showPassword });
                    }}
                  />
                </div>
              </form>
              <div>
                {loading ? (
                  <div
                    className="loader"
                    style={{
                      height: 60,
                      width: 60,
                      margin: "35px auto 35px"
                    }}
                  ></div>
                ) : (
                  <input
                    type="button"
                    name="button"
                    onClick={handleNext}
                    value="Get Started"
                    disabled={loading || this.state.transitioning}
                    className={`continueButton ${loading &&
                      "continueButtonLoading"}`}
                  />
                )}
              </div>
              {error && <div className="errorText mb-4">{error}</div>}
              <div className="font-open-sans">
                <span className="text-gray-600">Already have an account?</span>{" "}
                <a className="underline text-gray-600" href="/register/login">
                  Sign in
                </a>
              </div>
              <div className="mt-4">
                <span className="font-open-sans text-gray-600">
                  <i class="far fa-solid fa-shield-halved mr-2 text-purple-400"></i>
                  Protected with Bank Level Security.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
    const renderSignedIn = () => (
      <div
        className="stepContainer md:stepContainer-mobile sm:stepContainer-mobile"
        style={{ display: "block" }}
      >
        <Header className="md:hidden" />
        <div className="innerStepContainer xl:innerStepContainer-desktop lg:innerStepContainer-desktop md:innerStepContainer-mobile sm:innerStepContainer-mobile">
          <div id="stepHeader" className="md:pt-8 md:pb-3 md:py-8 w-full">
            <img
              src={logoBlack}
              style={{ padding: 0 }}
              className="hidden md:block mx-auto mt-12 w-auto"
            />
            {/*<Logo
              style={{ padding: 0 }}
              className="hidden md:block mx-auto mt-12 w-auto"
            />*/}
          </div>

          <div className="outerStack-mobile sm:outerStack xl:outerStack-mobile sm:outerStack-desktop lg:outerStack-mobile sm:outerStack-desktop md:outerStack-mobile sm:outerStack-mobile sm:outerStack-mobile sm:outerStack-mobile">
            <div className="innerStack">
              <img
                src={logoBlack}
                style={{ padding: 0 }}
                className="mx-auto mt-12 w-auto"
              />
              {/*<Logo style={{ padding: 0 }} className="mx-auto mt-12 w-auto" />*/}
              <div className="stepDescriptionText">
                Continue your Application
              </div>
              <input
                type="button"
                name="button"
                onClick={() => this.props.history.push("/resumeapplication")}
                value="Continue"
                className={`continueButton`}
              />
            </div>
            <div className="font-open-sans">
              <span className="text-gray-600">{`Welcome back${
                firstName || lastName
                  ? ` ${firstName || ""}${lastName ? ` ${lastName}` : ""}!`
                  : "!"
              }`}</span>{" "}
              <a
                className="underline text-gray-600"
                href="/register/login"
                onClick={() => window.localStorage.removeItem("jwt")}
              >
                Not you?
              </a>
            </div>
            <div className="mt-4">
              <span className="font-open-sans text-gray-600">
                <i class="far fa-solid fa-shield-halved mr-2 text-purple-400"></i>
                Protected with Bank Level Security.
              </span>
            </div>
          </div>
        </div>
      </div>
    );

    return signedIn && !this.state.transitioning
      ? renderSignedIn()
      : renderSignedOut();
  };

  renderDesktop = () => {
    const { handleNext } = this;
    const {
      internalError,
      errorFirstName,
      errorLastName,
      errorEmail,
      emailTip,
      errorPassword,
      errorTerms,
      showPassword
    } = this.state;
    const {
      updateUser,
      isMobile,
      user: { firstName, lastName, email, password, ppAccepted, termsAccepted },
      step: { error, loading }
    } = this.props;

    const acceptError = [termsAccepted, ppAccepted].filter(x => x).length !== 2;

    const signedIn = !!localStorage.getItem("jwt");

    const renderSignedOut = () => (
      <div
        className="h-full"
        onKeyDown={e => {
          if (isKey(e, ["enter"])) {
            console.log("yes it is entering...");
            handleNext();
          }
        }}
      >
        <div className="halfStepContainer bg-white">
          <SignupDescription imageClassName="py-4 mx-auto" />
        </div>
        <div
          className="halfStepContainer flex-col"
          style={{ display: "inline-flex", backgroundColor: "#f6f6f6" }}
        >
          <div className="flex flex-1">
            <div className="self-center w-full">
              <div id="stepHeader" className="py-0">
                <div className="hidden">
                  <Stepper activeStep={1} />
                </div>
                {/*<Logo className="mx-auto" />*/}
                <img src={logoBlack} className="mx-auto" />
              </div>
              <div id="stepContent" className="m-auto w-80percent md:mt-6">
                <div className="stepDescriptionText2">Create your account</div>
                <p className="pb-5 -mt-6 text-gray-800 md:hidden">
                  Good news! We don't pull your credit.
                </p>
                <form>
                  {errorFirstName && (
                    <div className="errorText">{errorFirstName}</div>
                  )}
                  <input
                    autoComplete="new-password"
                    type="text"
                    data-hj-allow
                    name="firstname"
                    value={firstName}
                    placeholder="Legal first name"
                    className={`inputField inputFieldText form-control data-hj-allow ${
                      errorFirstName ? "redBorder" : ""
                    }`}
                    onChange={e => {
                      let value = e.target.value;
                      value = value.replace(/[^A-Za-z]/ig, '');
                      updateUser({ firstName: value });
                    }}
                  ></input>
                  <br />
                  {errorLastName && (
                    <div className="errorText">{errorLastName}</div>
                  )}
                  <input
                    autoComplete="new-password"
                    type="text"
                    data-hj-allow
                    name="lastname"
                    value={lastName}
                    placeholder="Legal last name"
                    className={`inputField inputFieldText form-control data-hj-allow ${
                      errorLastName ? "redBorder" : ""
                    }`}
                    onChange={e => {
                      let value = e.target.value;
                      value = value.replace(/[^A-Za-z]/ig, '');
                      updateUser({ lastName: value });
                    }}
                  ></input>
                  <br />
                  {errorEmail && <div className="errorText">{errorEmail}</div>}
                  {emailTip && (
                    <div className="text-green-500 text-xs p-2">{emailTip}</div>
                  )}
                  <input
                    autoComplete="new-password"
                    type="text"
                    data-hj-allow
                    name="email"
                    value={email}
                    placeholder="Email"
                    title="Your email cannot be changed"
                    disabled={!!email}
                    className={`inputField inputFieldText form-control data-hj-allow ${
                      errorEmail ? "redBorder" : ""
                    } text-gray-700 cursor-not-allowed`}
                    onChange={e => {
                      updateUser({ email: e.target.value.toLowerCase() });
                    }}
                    onFocus={e => {
                      this.setState({ errorEmail: null, emailTip: null });
                    }}
                  ></input>
                  <br />
                  {errorPassword && (
                    <div className="errorText">{errorPassword}</div>
                  )}
                  {/* NOTE: Password show/hide button will overlap long passwords on small screens */}
                  <input
                    autoComplete="new-password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={password}
                    placeholder="Password"
                    className={`inputField inputFieldText ${
                      errorPassword ? "redBorder" : ""
                    }`}
                    onChange={e => {
                      updateUser({ password: e.target.value });
                    }}
                    onFocus={e => {
                      this.setState({ errorPassword: null });
                    }}
                  ></input>
                  <div
                    style={{
                      position: "relative",
                      display: "inline-block",
                      height: 0,
                      width: 0
                    }}
                  >
                    <ToggleVisibility
                      visible={showPassword}
                      onClick={() => {
                        this.setState({
                          showPassword: !this.state.showPassword
                        });
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 text-left px-4">
                    Must contain at least 8 characters and 1 letter and 1 symbol.
                  </p>
                </form>
                <div>
                  {loading ? (
                    <div
                      className="loader"
                      style={{
                        height: 60,
                        width: 60,
                        margin: "35px auto 35px"
                      }}
                    ></div>
                  ) : (
                    <input
                      type="button"
                      name="button"
                      onClick={handleNext}
                      value="Get Started"
                      disabled={loading || this.state.transitioning}
                      className={`continueButton ${
                        loading ? "continueButtonLoading" : ""
                      }`}
                    />
                  )}
                </div>
                {error && <div className="errorText mb-4">{error}</div>}
                <div className="font-open-sans">
                  <span className="text-gray-600">
                    Already have an account?
                  </span>{" "}
                  <a className="underline text-gray-600" href="/register/login">
                    Sign in
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="py-5">
            <span className="font-open-sans">
              <i class="far fa-solid fa-shield-halved mr-2 text-purple-400"></i>
              Protected with Bank Level Security.
            </span>
          </div>
        </div>
      </div>
    );
    const renderSignedIn = () => (
      <div
        className="h-full"
        onKeyDown={e => {
          if (isKey(e, ["enter"])) {
            handleNext();
          }
        }}
      >
        <div className="halfStepContainer bg-white">
          <SignupDescription imageClassName="py-4 mx-auto" />
        </div>
        <div
          className="halfStepContainer flex-col"
          style={{ display: "inline-flex", backgroundColor: "#f6f6f6" }}
        >
          <div className="flex flex-1">
            <div className="self-center w-full">
              <div id="stepHeader" className="py-0">
                <div className="hidden">
                  <Stepper activeStep={1} />
                </div>
                {/*<Logo className="mx-auto" />*/}
                <img src={logoBlack} className="mx-auto" />
              </div>
              <div id="stepContent" className="m-auto w-80percent mt-6">
                <div className="stepDescriptionText2">
                  Continue your Application
                </div>
                <input
                  type="button"
                  name="button"
                  onClick={() => this.props.history.push("/resumeapplication")}
                  value="Continue"
                  className={`continueButton cursor-pointer`}
                />
                <div className="font-open-sans">
                  <span className="text-gray-600">{`Welcome back${
                    firstName || lastName
                      ? ` ${firstName || ""}${lastName ? ` ${lastName}` : ""}!`
                      : "!"
                  }`}</span>{" "}
                  <a
                    className="underline text-gray-600"
                    href="/register/login"
                    onClick={() => window.localStorage.removeItem("jwt")}
                  >
                    Not you?
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="py-5">
            <span className="font-open-sans">
              <i class="far fa-solid fa-shield-halved mr-2 text-purple-400"></i>
              Protected with Bank Level Security.
            </span>
          </div>
        </div>
      </div>
    );

    return signedIn && !this.state.transitioning ? renderSignedIn() : renderSignedOut();
  };

  render() {
    const {
      renderMobile,
      renderDesktop,
      props: { isMobile }
    } = this;
    // TODO: Disable input fields upon step completion. Maybe put a nice green checkmark next to things :)
    return isMobile ? renderMobile() : renderDesktop();
  }
}

export default connect(
  state => ({
    user: state.register.user,
    step: state.register.basicInfo,
    termsAccepted: state.register.termsAccepted,
    ppAccepted: state.register.ppAccepted,
    hydrateStateWithToken: state.register.hydrateStateWithToken
  }),
  actions
)(BasicInfo);
